<template>
  <div id="view-log">
    <div class="title-bar">
      <a-icon type="arrow-left" @click="goBack" />
      <h1>View Log</h1>
    </div>

    <a-select
      v-if="!loadingVersions && versions.items.length > 1"
      :default-value="versions.latest.LeadGenId"
      id="select-version"
      @change="changeVersion"
    >
      <a-select-option
        v-for="version in versions.items"
        :key="version.LeadGenId"
      >{{ new Date(version.CreatedAt).toLocaleString() + ' - ' + getStatusText(version.StatusDate)}}</a-select-option>
    </a-select>

    <div class="main-info" v-if="!loadingData">
      <table>
        <tbody>
          <tr>
            <td class="label">Event Date</td>
            <td class="large-value">{{new Date(data.CreatedAt).toLocaleString()}}</td>
          </tr>
          <tr>
            <td class="label">Leadgen Id</td>
            <td
              class="large-value"
            >{{data.LeadGenId.substring(data.LeadGenId.indexOf("-")+1, data.LeadGenId.lastIndexOf("-"))}}</td>
          </tr>
          <tr>
            <td class="label">Event Status</td>
            <td
              class="large-value"
              :class="{error: data.StatusDate.includes('Err'), ok: data.StatusDate.includes('Processed')}"
            >{{getStatusText(data.StatusDate)}}</td>
          </tr>
          <tr>
            <td class="label">Original Event Notification Data</td>
            <td>
              <json-viewer :value="notificationData" copyable></json-viewer>
            </td>
          </tr>
          <tr v-if="data.FbHttpStatus != null">
            <td class="label">Data Request HTTP Status from Facebook</td>
            <td class="large-value">{{data.FbHttpStatus}}</td>
          </tr>
          <tr v-if="data.FbData != null">
            <td class="label">Data Received from Request to Facebook</td>
            <td>
              <json-viewer :value="fbData" copyable></json-viewer>
            </td>
          </tr>
          <tr>
            <td class="label">Mappings Used</td>
            <td>
              <json-viewer :value="mappingData" copyable :expand-depth="0"></json-viewer>
            </td>
          </tr>
          <tr>
            <td class="label">Data Sent To URL</td>
            <td>
              <json-viewer :value="dataSentToURL" copyable></json-viewer>
            </td>
          </tr>
          <tr>
            <td class="label">Data Sent To ICS</td>
            <td>
              <json-viewer :value="dataSent" copyable></json-viewer>
            </td>
          </tr>
          <tr>
            <td class="label">HTTP Status from ICS</td>
            <td class="large-value">{{data.SendHttpStatus}}</td>
          </tr>
          <tr>
            <td class="label">Response Received from ICS</td>
            <td>
              <json-viewer :value="sendResponse" copyable></json-viewer>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <a-spin size="large" v-if="loadingData"></a-spin>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

// convenience function that just returns an empty {} if the parsing fails
const parseJson = (jsonData) => {
  try {
    return JSON.parse(jsonData);
  } catch (e) {
    console.log("unable to parse", jsonData);
    // don't worry about the error
    return {};
  }
};

export default {
  computed: {
    ...mapState(["user", "apiUrl"]),
    dataSentToURL() {
      return "url" in parseJson(this.data.DataSent)
        ? parseJson(this.data.DataSent).url
        : {};
    },
    dataSent() {
      return "data" in parseJson(this.data.DataSent)
        ? parseJson(this.data.DataSent).data
        : {};
    },
    fbData() {
      return parseJson(this.data.FbData);
    },
    mappingData() {
      return parseJson(this.data.Mappings);
    },
    fbMappingData() {
      return parseJson(this.data.FBMappedKeys);
    },
    notificationData() {
      return parseJson(this.data.LeadRequest);
    },
    sendResponse() {
      return parseJson(this.data.SendResponse);
    },
  },
  data() {
    return {
      loadingVersions: true,
      loadingData: true,
      data: null,
      versions: [],
    };
  },
  methods: {
    changeVersion(val) {
      this.loadingData = true;

      axios({
        url: `${this.apiUrl}/logs`,
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          formId: this.formId,
          leadGenId: val,
          action: "LOG_PER_LEADGEN",
          processor: this.processor,
        },
      })
        .then((rslt) => {
          this.data = rslt.data;
          this.loadingData = false;
        })
        .catch((err) => {
          alert("Unable to load form");
          this.loadingData = false;
        });
    },
    getStatusText(code) {
      if (code.includes("Processed")) {
        return "Processed";
      } else if (code.includes("ErrFB")) {
        return "Error getting data from Facebook";
      } else if (code.includes("ErrInternal")) {
        return "Error processing data";
      } else if (code.includes("ErrICS")) {
        return "Error sending data to ICS";
      } else if (code.includes("ErrCountry")) {
        return "Error with value for country";
      } else if (code.includes("Test")) {
        return "Test Data";
      }
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    axios({
      url: `${this.apiUrl}/logs`,
      headers: {
        Authorization: this.user.signInUserSession.idToken.jwtToken,
      },
      params: {
        formId: this.formId,
        leadGenId: this.leadGenId.substr(2),
        action: "LOG_VERSIONS_PER_LEADGEN",
        processor: this.processor,
      },
    })
      .then((rslt) => {
        this.loadingVersions = false;
        this.versions = rslt.data;

        this.changeVersion(this.versions.latest.LeadGenId);
      })
      .catch((err) => {
        alert("Unable to load versions");
      });
  },
  props: ["formId", "leadGenId", "processor"],
};
</script>

<style lang="scss" scoped>
#view-log {
  display: flex;
  flex-direction: column;
}
.main-info {
  text-align: center;

  table {
    margin: 0 auto 40px;
  }
}
td {
  padding: 15px 10px;
  text-align: left;
  &.label {
    text-align: right;
  }
}
.label {
  font-size: 1.2em;
  font-weight: bold;
  vertical-align: top;
}
.large-value {
  font-size: 1.3em;
}
.error {
  text-transform: capitalize;
  background-color: #fff1f0;
}
.ok {
  text-transform: uppercase;
  background-color: #f6ffed;
}

#select-version {
  width: 800px;
  margin: 0 auto 30px;
}
</style>
